(function () {
  function getProductInfo(root) {
    const scope = root && root.querySelector ? root : document;
    if (scope.tagName === 'PRODUCT-INFO') return scope;
    return scope.querySelector('product-info') || scope.closest?.('product-info') || null;
  }

  function getPackSelectorRoot(root) {
    const productInfo = getProductInfo(root || document);
    const searchRoot = productInfo || root || document;
    if (!searchRoot.querySelector) return null;
    return (
      searchRoot.querySelector('.pack-selector[data-multi-pack="true"]') ||
      searchRoot.querySelector('.pack-selector')
    );
  }

  function parseTiersJson(raw) {
    if (!raw) return [];
    try {
      const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      return [];
    }
  }

  function getTiers(root) {
    const productInfo = getProductInfo(root);
    const selector = getPackSelectorRoot(root);
    const scope = root && root.querySelector ? root : document;
    const productPage =
      (scope.tagName === 'PRODUCT-PAGE' && scope) ||
      scope.closest?.('product-page') ||
      scope.querySelector?.('product-page') ||
      null;

    const fromRoot =
      root && typeof root.getAttribute === 'function'
        ? root.getAttribute('data-volume-discount-tiers')
        : null;
    if (fromRoot) {
      const tiers = parseTiersJson(fromRoot);
      if (tiers.length) return tiers;
    }

    const fromPage = productPage?.getAttribute('data-volume-discount-tiers');
    if (fromPage) {
      const tiers = parseTiersJson(fromPage);
      if (tiers.length) return tiers;
    }

    const fromInfo = productInfo?.getAttribute('data-volume-discount-tiers');
    if (fromInfo) {
      const tiers = parseTiersJson(fromInfo);
      if (tiers.length) return tiers;
    }

    const fromSelector = selector?.getAttribute('data-volume-discount-tiers');
    if (fromSelector) {
      const tiers = parseTiersJson(fromSelector);
      if (tiers.length) return tiers;
    }

    if (Array.isArray(window.ambrosiaVolumeDiscountTiers)) {
      return window.ambrosiaVolumeDiscountTiers;
    }

    return [];
  }

  function getSortedTiers(root) {
    return getTiers(root)
      .filter(function (t) {
        return t && Number.isFinite(parseInt(t.qty, 10));
      })
      .sort(function (a, b) {
        return parseInt(a.qty, 10) - parseInt(b.qty, 10);
      });
  }

  function getActiveTier(packQty, root) {
    const qty = Math.max(0, parseInt(packQty, 10) || 0);
    const tiers = getSortedTiers(root);

    let active = null;
    for (let i = 0; i < tiers.length; i++) {
      if (qty >= parseInt(tiers[i].qty, 10)) {
        active = tiers[i];
      }
    }
    return active;
  }

  function getNextTier(packQty, root) {
    const qty = Math.max(0, parseInt(packQty, 10) || 0);
    const tiers = getSortedTiers(root);

    for (let i = 0; i < tiers.length; i++) {
      if (qty < parseInt(tiers[i].qty, 10)) {
        return tiers[i];
      }
    }
    return null;
  }

  function getVolumeDiscountPercent(packQty, root) {
    const tier = getActiveTier(packQty, root);
    const pct = tier ? parseFloat(tier.percent) : 0;
    return pct > 0 ? pct : 0;
  }

  function getVolumeDiscountLabel(packQty, root) {
    const tier = getActiveTier(packQty, root);
    if (!tier) return '';
    const pct = parseFloat(tier.percent);
    if (!pct || pct <= 0) return '';
    const label = tier.discount_label || tier.discountLabel;
    if (label && String(label).trim()) return String(label).trim();
    return 'Save ' + Math.round(pct) + '%';
  }

  function normalizeUnitPrices(unitCentsOrArray, packQty) {
    if (Array.isArray(unitCentsOrArray)) {
      return unitCentsOrArray.filter(function (n) {
        return Number.isFinite(n);
      });
    }
    const unit = parseInt(unitCentsOrArray, 10);
    const qty = Math.max(1, parseInt(packQty, 10) || 1);
    if (!Number.isFinite(unit)) return [];
    return Array(qty).fill(unit);
  }

  function sumUnits(units) {
    return units.reduce(function (sum, unit) {
      return sum + unit;
    }, 0);
  }

  /**
   * Shopify Discount Functions: percentage is rounded per line unit, then summed.
   * e.g. $35.99 × 15% → $5.40 off → $30.59; ×2 = $61.18
   */
  function applyShopifyVolumeDiscount(units, root) {
    const pct = getVolumeDiscountPercent(units.length, root);
    if (!pct) return sumUnits(units);

    return units.reduce(function (sum, unit) {
      const discountCents = Math.round((unit * pct) / 100);
      return sum + (unit - discountCents);
    }, 0);
  }

  function applyVolumeDiscount(totalCents, packQty, root) {
    const cents = parseInt(totalCents, 10);
    const qty = Math.max(1, parseInt(packQty, 10) || 1);
    if (!Number.isFinite(cents) || cents <= 0) return 0;

    if (qty <= 1) {
      const pct = getVolumeDiscountPercent(1, root);
      if (!pct) return cents;
      return cents - Math.round((cents * pct) / 100);
    }

    const unit = Math.round(cents / qty);
    const units = Array(qty).fill(unit);
    return applyShopifyVolumeDiscount(units, root);
  }

  function applySellingPlanToUnitCents(unitCents, adjustment) {
    const unit = parseInt(unitCents, 10);
    if (!Number.isFinite(unit)) return 0;
    if (!adjustment) return unit;

    if (adjustment.value_type === 'percentage') {
      return Math.round((unit * (100 - adjustment.value)) / 100);
    }
    if (adjustment.value_type === 'fixed_amount') {
      return Math.max(0, unit - adjustment.value);
    }
    if (adjustment.value_type === 'price') {
      return adjustment.value;
    }

    return unit;
  }

  function computeOneTimeTotalCents(unitCentsOrArray, packQty, root) {
    const units = normalizeUnitPrices(unitCentsOrArray, packQty);
    if (!units.length) return 0;
    return applyShopifyVolumeDiscount(units, root);
  }

  function computeRetailTotalCents(unitCentsOrArray, packQty) {
    const units = normalizeUnitPrices(unitCentsOrArray, packQty);
    return sumUnits(units);
  }

  /**
   * Subscribe: SKIO/plan discount per unit (rounded) → volume discount per subscribed unit (rounded).
   */
  function computeSubscribeTotalCents(unitCentsOrArray, packQty, root, adjustment) {
    const units = normalizeUnitPrices(unitCentsOrArray, packQty);
    if (!units.length) return 0;

    const subscribedUnits = units.map(function (unit) {
      return applySellingPlanToUnitCents(unit, adjustment);
    });

    return applyShopifyVolumeDiscount(subscribedUnits, root);
  }

  window.ambrosiaPackVolumeDiscount = {
    getPercent: getVolumeDiscountPercent,
    getLabel: getVolumeDiscountLabel,
    getTiers: getTiers,
    getActiveTier: getActiveTier,
    getNextTier: getNextTier,
    apply: applyVolumeDiscount,
    computeOneTimeTotalCents: computeOneTimeTotalCents,
    computeRetailTotalCents: computeRetailTotalCents,
    computeSubscribeTotalCents: computeSubscribeTotalCents,
  };
})();
